import {
  CloseOutlined,
  DeleteTwoTone,
  EditTwoTone,
  SaveOutlined,
  SaveTwoTone,
} from '@ant-design/icons';
import { Button, Popconfirm, Tooltip } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { format } from 'date-fns';
import { CSSProperties, useEffect, useState } from 'react';
import useDeleteNote from '../../hooks/useDeleteNote';
import useEditNote from '../../hooks/useEditNote';
import { useNotify } from '../../hooks/useNotify';
import { onApolloError } from '../../lib/graphql/helpers';
import { lightTextColour } from '../../theme';
import { Optional } from '../Shared';
import type { RelatedNoteWithCreatedAtAndUpdatedAtAsNumber } from './types';
import { noteValidator } from './util';
import type { NoteContent, NoteShiftChange } from '../../hooks/types';

const smallTextStyle: CSSProperties = {
  fontSize: '.7rem',
  color: lightTextColour,
};

const noteButtonStyle: CSSProperties = {
  fontSize: '0.8rem',
  verticalAlign: 'bottom',
};

/**
 * Displays a note.
 * - The note header displays the author and the date the note was created.
 * - Alongside the header, there are buttons to edit or delete the note.
 * - The note body displays the content of the note.
 * - The note body also displays any shift changes recorded.
 */
const Note: React.FC<{
  /**
   * The note to display
   */
  readonly note: RelatedNoteWithCreatedAtAndUpdatedAtAsNumber;
  /**
   * Whether or not the note is read only
   */
  readonly readonly: boolean;
  /**
   * The ID of the user that is logged in
   * Used to determine whether or not the user can edit or delete the note
   */
  readonly loggedInUserId: string;
  /**
   * The background colour of the note
   */
  readonly backgroundColor: string;
  /**
   * Applies custom formatting to the note content before it is saved.
   * Use this to set the note structure in the database.
   */
  readonly formatOnSave: (note: string) => NoteContent;
  /**
   * Determines how the inner card content will be rendered for these notes.
   */
  readonly render: (
    note: RelatedNoteWithCreatedAtAndUpdatedAtAsNumber
  ) => JSX.Element;
}> = ({
  backgroundColor,
  formatOnSave,
  loggedInUserId,
  note,
  readonly,
  render,
}) => {
  const { notify } = useNotify();
  const [editMode, setEditMode] = useState(false);
  const [noteContent, setNoteContent] = useState(note.content ?? '');
  const [mutationEditNote] = useEditNote(onApolloError(notify.error));
  const [mutationDeleteNote] = useDeleteNote(onApolloError(notify.error));

  useEffect(() => {
    setNoteContent(note.content ?? '');
  }, [note]);

  const changes: readonly NoteShiftChange[] =
    note.metadata?.shift?.changes ?? [];

  const canEdit =
    // This note is not readonly
    !readonly &&
    // This note is not a changelog
    changes.length === 0 &&
    // This user created the note
    note.authorId === loggedInUserId;

  const onSaveNoteEdit = () => {
    const validatedNote = noteValidator(noteContent);
    if (validatedNote.isValid) {
      void mutationEditNote({
        variables: {
          noteId: note.id,
          content: formatOnSave(noteContent),
        },
        refetchQueries: 'active',
      });
      setEditMode(!editMode);
    } else {
      notify.error('Invalid note content');
    }
  };

  const onDeleteNote = (id: string) => {
    void mutationDeleteNote({
      variables: { noteId: id },
      refetchQueries: 'active',
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 10 }}>
      <div style={{ flexGrow: 1 }}>
        <div style={{ paddingTop: 10 }}>
          <div
            style={{
              color: lightTextColour,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex' }}>
              <p style={{ margin: 0, alignSelf: 'end', marginRight: 5 }}>
                {note.authorName ?? 'Unknown'}
              </p>
              <p style={{ margin: 0, alignSelf: 'end', marginRight: 5 }}>
                <em style={{ ...smallTextStyle, alignSelf: 'end' }}>
                  {format(note.createdAt, 'PPP HH:mm')}
                </em>
              </p>
            </div>

            {/* Edit/Delete Buttons */}
            <Optional show={canEdit}>
              <div>
                <Optional show={!editMode}>
                  <Tooltip title="Edit Note">
                    <Button type="text" onClick={() => setEditMode(!editMode)}>
                      <EditTwoTone style={noteButtonStyle} />
                    </Button>
                  </Tooltip>

                  <Popconfirm
                    title="Confirm Delete"
                    onConfirm={() => onDeleteNote(note.id)}
                    okText="Delete"
                    cancelText="Cancel"
                    overlayStyle={{ width: 'max-content' }}
                    placement="topRight"
                  >
                    {/* zIndex must be set less than the default of the Popconfirm to ensure it is not above it */}
                    <Tooltip title="Delete Note" zIndex={1000}>
                      <Button type="text">
                        <DeleteTwoTone
                          style={noteButtonStyle}
                          twoToneColor="red"
                        />
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                </Optional>

                <Optional show={editMode}>
                  <Tooltip title="Save Note">
                    <Button
                      type="text"
                      onClick={onSaveNoteEdit}
                      disabled={!noteValidator(noteContent).isValid}
                    >
                      {noteValidator(noteContent).isValid ? (
                        <SaveTwoTone style={noteButtonStyle} />
                      ) : (
                        <SaveOutlined style={noteButtonStyle} />
                      )}
                    </Button>
                  </Tooltip>

                  <Tooltip title="Cancel Edit">
                    <Button
                      type="text"
                      onClick={() => {
                        setEditMode(!editMode);
                        setNoteContent(note.content ?? '');
                      }}
                    >
                      <CloseOutlined
                        style={noteButtonStyle}
                        twoToneColor="red"
                      />
                    </Button>
                  </Tooltip>
                </Optional>
              </div>
            </Optional>
          </div>
        </div>

        {/* Main Content */}
        <div
          style={{
            width: '100%',
            backgroundColor: backgroundColor,
            padding: 10,
            borderRadius: 5,
          }}
        >
          {editMode && canEdit ? (
            <TextArea
              value={noteContent}
              onChange={(e) => setNoteContent(e.currentTarget.value)}
              maxLength={1000}
              showCount
            />
          ) : (
            render(note)
          )}

          {/* Last Edited */}
          <Optional show={note.updatedAt !== note.createdAt}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <em style={smallTextStyle}>
                Edited: {format(note.updatedAt, 'PPP HH:mm')}
              </em>
            </div>
          </Optional>
        </div>
      </div>
    </div>
  );
};

export default Note;
